// Requires Promise and Array polyfills for older browsers
/**
 * Stores extracted CSS custom properties after the first retrieval from the DOM.
 *
 * @remarks
 * This variable caches CSS variable values to improve performance on browsers without native CSS variable support.
 * Once populated, subsequent calls to {@link getCssVariableForOldBrowsers} will use this cached data instead of re-parsing the DOM.
 */
let savedVariables: { [key: string]: string } | null = null;

/**
 * Retrieves all style elements and stylesheet link elements from the document.
 *
 * @returns A NodeList containing all uninserted style elements and external stylesheet links.
 *
 * @remarks
 * Filters out previously inserted styles using `.inserted` class to avoid processing duplicates.
 * This ensures only the original stylesheets are parsed for CSS variable extraction.
 */
function findStyleBlocks(): NodeListOf<Element> {
  return document.querySelectorAll('style:not(.inserted),link[rel="stylesheet"]');
}

/**
 * Extracts the text content from multiple style blocks in parallel.
 *
 * @param styleBlocks - The style elements and stylesheet links to process.
 * @returns A promise resolving to an array of CSS text from all style blocks.
 *
 * @remarks
 * Uses Promise.all for parallel processing of all blocks.
 * Individual block errors are silently ignored to allow processing of remaining blocks.
 */
async function getStyleBlocksTexts(styleBlocks: NodeListOf<Element>) {
  return Promise.all(Array.from(styleBlocks).map((block) => getStyleBlockText(block).catch(ignoreErrorForBlock)));
}

/**
 * Handles errors during style block processing by logging and returning empty string.
 *
 * @param err - The error that occurred during processing.
 * @returns An empty string to allow processing to continue for other blocks.
 *
 * @remarks
 * This error handler ensures that failures in processing one style block do not prevent extraction from other blocks.
 * Errors are logged to the console for debugging purposes.
 */
// the error for specific block should be ignored, to enable search in other blocks
function ignoreErrorForBlock(err: Error): '' {
  console.error(err);

  return '';
}

/**
 * Retrieves the CSS text content from a single style element or stylesheet link.
 *
 * @param styleBlock - The style element or link element to process.
 * @returns A promise resolving to the CSS text content.
 *
 * @remarks
 * Handles two types of elements: inline `<style>` tags and external `<link rel="stylesheet">` stylesheets.
 * External stylesheets are fetched via XMLHttpRequest to maintain browser compatibility.
 */
async function getStyleBlockText(styleBlock: Element): Promise<string> {
  switch (styleBlock.nodeName) {
    case 'STYLE':
      return getTextForStyleNode(styleBlock);
    case 'LINK':
      return getTextForLinkNode(styleBlock);
    default:
      return '';
  }
}

/**
 * Extracts the CSS text from an inline style element.
 *
 * @param styleBlock - The inline style element.
 * @returns The text content of the style block.
 */
function getTextForStyleNode(styleBlock: Element): string {
  return styleBlock.innerHTML;
}

/**
 * Fetches the CSS text from an external stylesheet via XMLHttpRequest.
 *
 * @param styleBlock - The link element with the stylesheet URL.
 * @returns A promise resolving to the CSS text from the external stylesheet.
 *
 * @remarks
 * Uses XMLHttpRequest (instead of fetch) for broader browser compatibility, including older browsers.
 * Returns empty string if the href attribute is missing.
 * Rejects if the server responds with an error status or network error occurs.
 */
async function getTextForLinkNode(styleBlock: Element): Promise<string> {
  const url: string | null = styleBlock.getAttribute('href');
  if (!url) {
    return Promise.resolve('');
  }

  return new Promise<string>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('GET', url, true);
    request.overrideMimeType('text/css;');
    request.onload = function () {
      if (request.status >= 200 && request.status < 400) {
        resolve(request.responseText);
      } else {
        reject(`Error returned from: ${url}`);
      }
    };

    request.onerror = function () {
      reject(`Got nothing from: ${url}`);
    };

    request.send();
  });
}

/**
 * Parses CSS text and extracts all CSS custom property (variable) declarations.
 *
 * @param css - The CSS text to parse.
 * @returns An array of matched CSS variable declarations, or null if none found.
 *
 * @remarks
 * Matches CSS variables that follow the `--name: value;` pattern.
 * Uses regex to handle both minified and unminified CSS formats.
 * Returns raw matches without processing; use {@link reduceVariablesList} to convert to a key-value map.
 */
function extractVariablesListFromCssText(css: string): RegExpMatchArray | null {
  return css.match(/(-{2}\w[\w-]*)(?:\s*:\s*)([^;]*)(;|})/g);
}

/**
 * Converts an array of raw CSS variable matches into a key-value object.
 *
 * @param variablesList - The raw CSS variable matches from {@link extractVariablesListFromCssText}.
 * @returns An object mapping CSS variable names (e.g., `--color-red`) to their values.
 *
 * @remarks
 * Handles edge cases in minified CSS where the trailing semicolon is omitted by Angular CLI.
 * For example, minified CSS like `.chart-colors{--chart-color-6:#c22327;--chart-color-2:#00a1e0}.chart-positive-color{color:#2e7d32}`
 * requires special parsing to extract the correct value for `--chart-color-2` (which would otherwise include the next selector).
 * Uses `}` as an additional delimiter to ensure accurate value extraction in minified CSS.
 */
function reduceVariablesList(variablesList: RegExpMatchArray) {
  return variablesList.reduce(
    (variables, variable) => {
      const variablesWithValues = variable.split(/;\s*/);

      return {
        ...variables,
        ...variablesWithValues.reduce(
          (variableSet, variableWithValue) => {
            const splitVariable = variableWithValue.split(/:\s*/);
            // Note: For minified css files, the semicolon from last declaration statement is removed by angular cli
            // for optimization purposes. For example below will be sample code in minified css file,
            // :root{--chart-color-6:#c22327;--chart-color-2:#00a1e0}.chart-positive-color{color:#2e7d32}
            // variableWithValue will gives us something like below for '--chart-color-2':-
            // "--chart-color-2: #00a1e0}.chart-positive-color{color:#2e7d32"
            // console.log(splitVariable); // ["--chart-color-2", "#00a1e0}.chart-positive-color{color:#2e7d32"]
            // splitVariable has wrong value for --chart-color-2 variable.
            // we need to split the splitVariable further with "}" so that we can get the right value.
            const variableValue = splitVariable[1] ? splitVariable[1].split('}')[0] : splitVariable[1];

            return {
              ...variableSet,
              [splitVariable[0]]: variableValue,
            };
          },
          {} as { [key: string]: string },
        ),
      };
    },
    {} as { [key: string]: string },
  );
}

/**
 * Retrieves a CSS custom property value for browsers without native CSS variable support.
 *
 * @param name - The CSS variable name (e.g., `--primary-color`).
 * @returns A promise resolving to the variable value, or undefined if not found.
 *
 * @remarks
 * This function is automatically called when {@link modernCSSIsSupported} returns false.
 * It caches extracted variables in {@link savedVariables} to avoid re-parsing the DOM on subsequent calls.
 * Parses both inline `<style>` elements and external stylesheet links, handling both minified and unminified CSS.
 */
async function getCssVariableForOldBrowsers(name: string): Promise<string | undefined> {
  if (savedVariables) {
    return savedVariables[name];
  }

  const styleBlocks = findStyleBlocks();
  if (!styleBlocks || !styleBlocks.length) {
    return Promise.resolve(undefined);
  }

  const styleBlocksTexts = await getStyleBlocksTexts(styleBlocks);
  const variablesList = extractVariablesListFromCssText(styleBlocksTexts.join(''));
  if (!variablesList || !variablesList.length) {
    return Promise.resolve(undefined);
  }

  const cssVariables = reduceVariablesList(variablesList);
  savedVariables = cssVariables;

  return cssVariables[name];
}

/**
 * Retrieves a CSS custom property value using the browser's native CSS support.
 *
 * @param name - The CSS variable name (e.g., `--primary-color`).
 * @param elementName - The selector of the element to query (defaults to `:root` for global variables).
 * @returns The computed value of the CSS variable, or empty string if not found.
 *
 * @remarks
 * Uses `getComputedStyle` to retrieve the computed value of the CSS custom property.
 * Returns an empty string (not undefined) if the element is not found in the DOM.
 */
function getCssVariableForModernBrowsers(name: string, elementName: string = ':root'): string {
  const element = document.querySelector(elementName);
  if (!element) {
    return '';
  }
  const computedStyle = getComputedStyle(element);

  return computedStyle.getPropertyValue(name);
}

/**
 * Retrieves the value of a CSS custom property (CSS variable), with automatic browser compatibility handling.
 *
 * @param name - The CSS variable name (e.g., `--primary-color`).
 * @param elementName - The selector of the element to query (defaults to `:root` for global variables).
 * @param searchEverywhere - If true, forces the old-browser fallback method; defaults to false.
 * @returns A promise resolving to the CSS variable value, or undefined if not found.
 *
 * @remarks
 * Automatically detects native CSS variable support using {@link modernCSSIsSupported}.
 * On modern browsers, uses `getComputedStyle` for direct access.
 * On older browsers (IE), parses stylesheets and returns cached results for performance.
 * Set `searchEverywhere` to true to force DOM parsing even on modern browsers.
 *
 * @example
 *
 * ```typescript
 * // Get a global CSS variable from :root
 * const primaryColor = await getCssVariable('--primary-color');
 *
 * // Get a scoped CSS variable from a specific element
 * const buttonColor = await getCssVariable('--button-color', '.button-element');
 *
 * // Force parsing on modern browsers
 * const fallbackValue = await getCssVariable('--custom-var', ':root', true);
 * ```
 */
export async function getCssVariable(
  name: string,
  elementName: string = ':root',
  searchEverywhere = false,
): Promise<string | undefined> {
  if (!searchEverywhere && modernCSSIsSupported()) {
    return getCssVariableForModernBrowsers(name, elementName);
  }

  return getCssVariableForOldBrowsers(name);
}

/**
 * Checks if the browser supports CSS custom properties (CSS variables) natively.
 *
 * @returns true if the browser supports CSS variables, false otherwise.
 *
 * @remarks
 * Uses `CSS.supports()` method for feature detection.
 * Returns false for older browsers like Internet Explorer that lack CSS variable support.
 */
export function modernCSSIsSupported() {
  const CSS = (window as any).CSS;

  return !!(CSS && CSS.supports);
}
